import { useState, useEffect } from 'react';
import { logEvent } from '@amplitude/analytics-browser';
import { Trans } from 'react-i18next';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '../../componentsv2/Typography';
import Button from '../../componentsv2/Button';
import Section from '../../componentsv2/Section';
import CopyToClipboardButton from '../../componentsv2/CopyToClipboardButton';
import TwoFactorAuthInput from '../../componentsv2/TwoFactorAuthInput';
import Modal from '../../componentsv2/Modal';
import {
  DownloadIcon,
  InfoCircleIcon,
  LockIcon,
  ShieldCheckIcon,
  ShieldKeyIcon,
} from '../../components/Icons';
import { confirmTfa, set2Fa } from '../../services/auth';
import { useAppDispatch, useAppSelector, setTfaValidated, setUser } from '../../store';
import { useTranslation } from '../../hooks/i18n';
import { twoFactorEvents } from '../../constants/events';
import { Nullable } from '../../types';
import {
  ACCESS_TOKEN_STORAGE_KEY,
  REFRESH_TOKEN_STORAGE_KEY,
  TFA_VALIDATED_STORAGE_KEY,
} from '../../constants/auth';
import { COLORS } from '../../themev2';

const steps = ['introduction', 'qrCode', 'verification', 'success'];

interface TwoFactorSetupModalProps {
  isOpen: boolean;
  handleClose: () => void;
  onSuccess?: () => void;
}

const TwoFactorSetupModal = ({ isOpen, handleClose, onSuccess }: TwoFactorSetupModalProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const [currentStep, setCurrentStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Nullable<string>>(null);
  const [qrCodeUrl, setQrCodeUrl] = useState<string>('');
  const [secretKey, setSecretKey] = useState<string>('');
  const [verificationCode, setVerificationCode] = useState(['', '', '', '', '', '']);
  const [showManualCode, setShowManualCode] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState<string>('');

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(0);
      setError(null);
      // setRememberDevice(false);
      setVerificationCode(['', '', '', '', '', '']);
      setQrCodeUrl('');
      setSecretKey('');
    }
  }, [isOpen]);

  const handleVerifyCode = async () => {
    logEvent(twoFactorEvents.CLICK_VERIFY_CODE);

    const code = verificationCode.join('');
    if (code.length !== 6) {
      setError(t('myAccount:security.twoFactor.invalidCodeError'));
      return;
    }

    try {
      setIsLoading(true);
      const response = await confirmTfa(code);

      if (!response.confirmed) {
        throw new Error('Invalid 2FA code');
      }

      if (response.tokens) {
        localStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, response.tokens.access);
        localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, response.tokens.refresh);
      }

      localStorage.setItem(TFA_VALIDATED_STORAGE_KEY, 'true');
      dispatch(setTfaValidated(true));
      dispatch(setUser({ ...user, tfaEnabled: true }));

      if (response.recoveryCode) {
        setRecoveryCode(response.recoveryCode);
      }

      // Move to success step
      setCurrentStep(3);
      logEvent(twoFactorEvents.TWO_FACTOR_VERIFICATION_SUCCESS);
    } catch (error) {
      logEvent(twoFactorEvents.TWO_FACTOR_VERIFICATION_ERROR, { Error: error });
      setError(t('myAccount:security.twoFactor.verificationError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleStartTwoFactorSetup = async () => {
    try {
      setIsLoading(true);
      logEvent(twoFactorEvents.CLICK_SET_UP_NOW);
      setCurrentStep(1);

      const response = await set2Fa();

      setQrCodeUrl(response.qrCode);
      setSecretKey(response.secret);
      logEvent(twoFactorEvents.RETRIEVE_QR_CODE_SUCCESS);
    } catch (error) {
      setError(t('myAccount:security.twoFactor.qrCodeGenerationError'));
      logEvent(twoFactorEvents.RETRIEVE_QR_CODE_ERROR, { Error: error });
    } finally {
      setIsLoading(false);
    }
  };

  const handleNextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePreviousStep = () => {
    logEvent(twoFactorEvents.CLICK_PREVIOUS);
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleFinish = () => {
    logEvent(twoFactorEvents.CLICK_DONE);
    localStorage.setItem(TFA_VALIDATED_STORAGE_KEY, 'true');
    dispatch(setTfaValidated(true));
    dispatch(setUser({ ...user, tfaEnabled: true }));
    if (onSuccess) {
      onSuccess();
    }
  };

  // Introduction Step
  const renderIntroductionStep = () => (
    <Grid container rowSpacing={3}>
      <Grid item xs={12}>
        <Typography variant="h5" sx={{ fontWeight: 600, marginBottom: '1rem' }}>
          {t('myAccount:security.twoFactorSetupModal.setupTitle')}
        </Typography>
        <Typography variant="body1" sx={{ textAlign: 'left', color: COLORS.gray[1100] }}>
          {t('myAccount:security.twoFactorSetupModal.setupDescription')}
        </Typography>
      </Grid>
      <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <Section sx={{ background: COLORS.gray[300], padding: '1rem', boxShadow: 'none' }}>
          <Grid container sx={{ display: 'flex', alignItems: 'center' }}>
            <Grid item xs={2} sx={{ display: 'flex', justifyContent: 'center' }}>
              <LockIcon />
            </Grid>
            <Grid item xs={10} sx={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <Typography
                sx={{
                  textAlign: 'left',
                  color: COLORS.gray[1200],
                  fontSize: '16px',
                  fontWeight: 600,
                  lineHeight: '24px',
                }}
              >
                {t('myAccount:security.twoFactorSetupModal.secureAuthenticationTitle')}
              </Typography>
              <Typography
                sx={{
                  textAlign: 'left',
                  color: COLORS.gray[1100],
                  fontSize: '14px',
                  fontWeight: 400,
                  lineHeight: '21px',
                }}
              >
                {t('myAccount:security.twoFactorSetupModal.secureAuthenticationDescription')}
              </Typography>
            </Grid>
          </Grid>
        </Section>
        <Section sx={{ background: COLORS.gray[300], padding: '1rem', boxShadow: 'none' }}>
          <Grid container sx={{ display: 'flex', alignItems: 'center' }}>
            <Grid item xs={2} sx={{ display: 'flex', justifyContent: 'center' }}>
              <ShieldKeyIcon />
            </Grid>
            <Grid item xs={10} sx={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <Typography
                sx={{
                  textAlign: 'left',
                  color: COLORS.gray[1200],
                  fontSize: '16px',
                  fontWeight: 600,
                  lineHeight: '24px',
                }}
              >
                {t('myAccount:security.twoFactorSetupModal.advancedProtectionTitle')}
              </Typography>
              <Typography
                sx={{
                  textAlign: 'left',
                  color: COLORS.gray[1100],
                  fontSize: '14px',
                  fontWeight: 400,
                  lineHeight: '21px',
                }}
              >
                {t('myAccount:security.twoFactorSetupModal.advancedProtectionDescription')}
              </Typography>
            </Grid>
          </Grid>
        </Section>
      </Grid>
      <Grid item xs={6} md={4}>
        <Button
          fullWidth
          type="link"
          color="tertiary"
          onClick={() => {
            logEvent(twoFactorEvents.CLICK_SET_UP_LATER);
            handleClose();
          }}
        >
          {t('myAccount:security.twoFactorSetupModal.cancelButton')}
        </Button>
      </Grid>
      <Grid item xs={6} md={8}>
        <Button fullWidth color="secondary" onClick={handleStartTwoFactorSetup}>
          {t('myAccount:security.twoFactorSetupModal.startButton')}
        </Button>
      </Grid>
    </Grid>
  );

  // QR Code Step
  const renderQrCodeStep = () => (
    <Grid container rowSpacing={3}>
      <Grid item xs={12}>
        <Typography variant="h5" sx={{ fontWeight: 600, marginBottom: '1rem' }}>
          {t('myAccount:security.twoFactorSetupModal.scanQrCodeTitle')}
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Section sx={{ background: COLORS.gray[700], padding: '1.5rem', boxShadow: 'none' }}>
          <Grid container alignItems="center" rowSpacing={1}>
            <Grid
              item
              xs={12}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: { xs: '8px' },
              }}
            >
              <InfoCircleIcon />
              <Typography
                sx={{ color: COLORS.brand.night, fontSize: '16px', fontWeight: 600, lineHeight: '24px' }}
              >
                {t('myAccount:security.twoFactorSetupModal.scanQrCodeWhatIsGoogleAuthenticatorTitle')}
              </Typography>
            </Grid>
            <Grid item xs={12} sx={{ marginLeft: { xs: '32px' } }}>
              <Typography
                sx={{ color: COLORS.brand.night, fontSize: '16px', fontWeight: 400, lineHeight: '24px' }}
              >
                {t('myAccount:security.twoFactorSetupModal.scanQrCodeWhatIsGoogleAuthenticatorDescription')}
              </Typography>
            </Grid>
          </Grid>
        </Section>
      </Grid>
      <Grid item xs={12}>
        <Grid container>
          <Grid item xs={12} md={4} lg={3}>
            <Box
              sx={{
                border: `1px solid ${COLORS.gray[200]}`,
                boxShadow: '0px 1px 2px 0px rgba(16, 27, 155, 0.05)',
                borderRadius: '12px',
                padding: '1rem',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                background: COLORS.brand.white,
                width: '188px',
                height: '188px',
              }}
            >
              {isLoading ? (
                <Box sx={{ width: '168px', height: '168px', backgroundColor: COLORS.gray[200] }} />
              ) : (
                <img src={qrCodeUrl} alt="QR Code" width="168px" height="168px" />
              )}
            </Box>
          </Grid>
          <Grid item xs={12} md={8} lg={9}>
            <Grid container rowSpacing={2}>
              <Grid item xs={12}>
                <Typography
                  sx={{ fontSize: '16px', fontWeight: 600, lineHeight: '24px', color: COLORS.gray[1200] }}
                >
                  {t('myAccount:security.twoFactorSetupModal.scanQrCodeStepTitle')}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Grid container rowSpacing={1}>
                  <Grid item xs={12}>
                    <Typography sx={{ fontSize: '16px', fontWeight: 400, lineHeight: '24px' }}>
                      <Trans
                        t={t}
                        i18nKey={'myAccount:security.twoFactorSetupModal.scanQrCodeStepOne'}
                        components={{
                          span: <span style={{ color: COLORS.indigo[500], fontWeight: 600 }}></span>,
                        }}
                      >
                        1. Download the <span>Google Authenticator app</span>
                      </Trans>
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography sx={{ fontSize: '16px', fontWeight: 400, lineHeight: '24px' }}>
                      {t('myAccount:security.twoFactorSetupModal.scanQrCodeStepTwo')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography sx={{ fontSize: '16px', fontWeight: 400, lineHeight: '24px' }}>
                      {t('myAccount:security.twoFactorSetupModal.scanQrCodeStepThree')}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} md={showManualCode ? 7 : 12}>
                {showManualCode ? (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      backgroundColor: COLORS.gray[400],
                      padding: '1rem',
                      borderRadius: '8px',
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: 'monospace',
                        fontWeight: 500,
                        fontSize: '14px',
                        letterSpacing: '0.5px',
                        flex: 1,
                        wordBreak: 'break-all',
                      }}
                    >
                      {secretKey}
                    </Typography>
                    <CopyToClipboardButton
                      textToCopy={secretKey}
                      customSX={{ paddingY: 0 }}
                      onClick={() => {
                        logEvent(twoFactorEvents.CLICK_COPY_SECRET_KEY);
                      }}
                    />
                  </Box>
                ) : (
                  <Typography sx={{ fontSize: '16px', fontWeight: 400, lineHeight: '24px' }}>
                    <Trans
                      t={t}
                      i18nKey={'myAccount:security.twoFactorSetupModal.scanQrCodeManually'}
                      components={{
                        span: (
                          <span
                            style={{
                              color: COLORS.indigo[500],
                              fontWeight: 600,
                              textDecoration: 'underline',
                              cursor: 'pointer',
                            }}
                            onClick={() => {
                              setShowManualCode(!showManualCode);
                              logEvent(twoFactorEvents.CLICK_MANUAL_CODE_TOGGLE, {
                                showManualCode: !showManualCode,
                              });
                            }}
                          ></span>
                        ),
                      }}
                    >
                      Can't scan it? <span>Enter code manually</span>
                    </Trans>
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={6} md={4}>
        <Button
          fullWidth
          type="link"
          color="tertiary"
          onClick={() => {
            logEvent(twoFactorEvents.CLICK_CANCEL_SET_UP);
            handleClose();
          }}
        >
          {t('myAccount:security.twoFactorSetupModal.scanQrCodeCancelButton')}
        </Button>
      </Grid>
      <Grid item xs={6} md={8}>
        <Button
          fullWidth
          color="secondary"
          onClick={() => {
            logEvent(twoFactorEvents.CLICK_NEXT);
            handleNextStep();
          }}
        >
          {t('myAccount:security.twoFactorSetupModal.scanQrCodeNextButton')}
        </Button>
      </Grid>
    </Grid>
  );

  // Verification Code Step
  const renderVerificationStep = () => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '470px',
        justifyContent: 'space-between',
      }}
    >
      <Grid container rowSpacing={3}>
        <Grid item xs={12}>
          <Typography variant="h5" sx={{ fontWeight: 600, marginBottom: '1rem' }}>
            {t('myAccount:security.twoFactorSetupModal.scanQrCodeTitle')}
          </Typography>
        </Grid>
        <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <Typography
            sx={{
              textAlign: 'left',
              color: COLORS.gray[1200],
              fontSize: '18px',
              fontWeight: 600,
              lineHeight: '27px',
            }}
          >
            {t('myAccount:security.twoFactorSetupModal.enterGoogleAuthenticatorCodeTitle')}
          </Typography>
          <Typography
            sx={{
              textAlign: 'left',
              color: COLORS.gray[1100],
              fontSize: '16px',
              fontWeight: 400,
              lineHeight: '24px',
            }}
          >
            <Trans
              t={t}
              i18nKey={'myAccount:security.twoFactorSetupModal.enterGoogleAuthenticatorCodeDescription'}
              components={{
                span: <span style={{ color: COLORS.indigo[500], fontWeight: 600 }}></span>,
              }}
            >
              Please enter your 6-digit authentication code generated by the{' '}
              <span>Google Authenticator app</span>
            </Trans>
          </Typography>
        </Grid>
        <TwoFactorAuthInput
          onSubmit={handleVerifyCode}
          verificationCode={verificationCode}
          setVerificationCode={setVerificationCode}
          error={error}
          onError={setError}
        />
        <Grid item xs={6} md={4}>
          <Button fullWidth type="link" color="tertiary" onClick={handlePreviousStep}>
            {t('myAccount:security.twoFactorSetupModal.enterGoogleAuthenticatorCodeBackButton')}
          </Button>
        </Grid>
        <Grid item xs={6} md={8}>
          <Button
            fullWidth
            color="secondary"
            disabled={!verificationCode.every((digit) => digit !== '')}
            isLoading={isLoading}
            onClick={handleVerifyCode}
          >
            {t('myAccount:security.twoFactorSetupModal.enterGoogleAuthenticatorCodeNextButton')}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );

  // Success Step
  const renderSuccessStep = () => (
    <Grid container rowSpacing={3}>
      <Grid item xs={12}>
        <Typography variant="h5" sx={{ fontWeight: 600, marginBottom: '1rem' }}>
          {t('myAccount:security.twoFactorSetupModal.scanQrCodeTitle')}
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Grid container alignItems="center" spacing={4}>
          <Grid item xs="auto">
            <ShieldCheckIcon />
          </Grid>
          <Grid item xs={10}>
            <Typography
              sx={{
                fontSize: '24px',
                fontWeight: 700,
                lineHeight: '36px',
                background: 'linear-gradient(272.4deg, #0BAAA1 11.6%, #0B8DAA 87.19%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                marginBottom: '0.5rem',
              }}
            >
              {t('myAccount:security.twoFactorSetupModal.successTitle')}
            </Typography>
            <Typography
              sx={{
                fontSize: '20px',
                fontWeight: 400,
                lineHeight: '30px',
                color: COLORS.gray[1100],
              }}
            >
              {t('myAccount:security.twoFactorSetupModal.successDescription')}
            </Typography>
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12} sx={{ marginTop: '1.5rem' }}>
        <Box
          sx={{
            border: `1px solid ${COLORS.gray[700]}`,
            borderRadius: '12px',
            padding: '1.5rem',
            background: COLORS.gray[50],
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography
                sx={{
                  fontSize: '16px',
                  fontWeight: 600,
                  lineHeight: '24px',
                  color: COLORS.gray[1200],
                  marginBottom: '0.5rem',
                }}
              >
                {t('myAccount:security.twoFactorSetupModal.recoveryCodes')}
              </Typography>
              <Typography
                sx={{
                  fontSize: '14px',
                  lineHeight: '21px',
                  color: COLORS.gray[1100],
                  marginBottom: '1rem',
                }}
              >
                {t('myAccount:security.twoFactorSetupModal.recoveryCodesDescription')}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={8} md={6}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.5rem',
                  backgroundColor: COLORS.brand.white,
                  padding: '0.75rem 1rem',
                  borderRadius: '6px',
                  border: `1px solid ${COLORS.gray[700]}`,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: 'monospace',
                    fontWeight: 500,
                    fontSize: '14px',
                    letterSpacing: '0.5px',
                    color: COLORS.gray[1200],
                  }}
                >
                  {recoveryCode}
                </Typography>
              </Box>
            </Grid>
            <Grid
              item
              xs={12}
              sm={4}
              md={6}
              sx={{
                display: 'flex',
                justifyContent: 'flex-start',
                flexDirection: 'row',
                alignItems: 'end',
                gap: '1rem',
              }}
            >
              <CopyToClipboardButton
                textToCopy={recoveryCode}
                onClick={() => {
                  logEvent(twoFactorEvents.CLICK_COPY_RECOVERY_CODES);
                }}
                text={t('myAccount:security.twoFactorSetupModal.copyRecoveryCodes')}
              />
              <Button
                isFullHeight={false}
                type="outline"
                color="tertiary"
                onClick={() => {
                  const element = document.createElement('a');
                  const file = new Blob([recoveryCode], { type: 'text/plain' });
                  element.href = URL.createObjectURL(file);
                  element.download = `${t(
                    'myAccount:security.twoFactorSetupModal.recoveryCodesDocument'
                  )}.txt`;
                  document.body.appendChild(element);
                  element.click();
                  document.body.removeChild(element);
                  logEvent(twoFactorEvents.CLICK_DOWNLOAD_RECOVERY_CODES);
                }}
                startIcon={<DownloadIcon />}
              >
                {t('myAccount:security.twoFactorSetupModal.downloadRecoveryCodes')}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Grid>
      <Grid item xs={12}>
        <Button fullWidth color="secondary" onClick={handleFinish}>
          {t('myAccount:security.twoFactorSetupModal.successFinishButton')}
        </Button>
      </Grid>
    </Grid>
  );

  // Render the current step
  const renderCurrentStep = () => {
    switch (currentStep) {
      case 0:
        return renderIntroductionStep();
      case 1:
        return renderQrCodeStep();
      case 2:
        return renderVerificationStep();
      case 3:
        return renderSuccessStep();
      default:
        return null;
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      handleClose={() => {
        if (currentStep === 3) {
          handleFinish();
        } else {
          handleClose();
        }
      }}
      size={currentStep === 0 ? 'medium' : 'large'}
      isAutoHeight={currentStep === 0 || currentStep === 3}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          height: currentStep === 0 || currentStep === 3 ? 'auto' : '470px',
        }}
      >
        {renderCurrentStep()}
      </Box>
    </Modal>
  );
};

export default TwoFactorSetupModal;
